// ============================================================================
// AVATAR REVIEW QUEUE PAGE
// Admin interface for reviewing avatar publishing submissions
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle, XCircle, Eye, Clock, TrendingUp, ChevronRight,
  AlertCircle, Users, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  AvatarReviewQueueEntry,
  AvatarDefinition
} from '../types';
import {
  getPendingReviews,
  getReviewWithAvatar,
  approveReview,
  rejectReview,
  getOrgReviewStats
} from '../services/avatarCatalogService';

export default function AvatarReviewQueue() {
  const { user } = useAuth();

  const [reviews, setReviews] = useState<AvatarReviewQueueEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any>(null);

  const [selectedReview, setSelectedReview] = useState<AvatarReviewQueueEntry | null>(null);
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarDefinition | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const organizationId = user?.organizationId || '';

  const fetchReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getPendingReviews(organizationId);
      setReviews(result.reviews);
    } catch (err: any) {
      setError(err.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  const fetchStats = useCallback(async () => {
    try {
      const result = await getOrgReviewStats(organizationId);
      setStats(result.stats);
    } catch (err: any) {
      console.error('Failed to load stats:', err);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchReviews();
    fetchStats();
  }, [fetchReviews, fetchStats]);

  const handleViewDetails = async (review: AvatarReviewQueueEntry) => {
    try {
      const result = await getReviewWithAvatar(review.reviewId);
      setSelectedReview(result.review);
      setSelectedAvatar(result.avatar);
      setShowDetailModal(true);
    } catch (err: any) {
      alert(`Failed to load review details: ${err.message}`);
    }
  };

  const handleApprove = async (review: AvatarReviewQueueEntry) => {
    const notes = window.prompt(`Approve "${review.avatarDefinitionId}"? Add review notes (optional):`);
    if (notes === null) return;
    try {
      await approveReview(review.avatarDefinitionId, review.reviewId, user?.userId || '', notes || undefined);
      alert('Avatar approved and added to organization catalog!');
      fetchReviews();
      fetchStats();
      setShowDetailModal(false);
    } catch (err: any) {
      alert(`Failed to approve: ${err.message}`);
    }
  };

  const handleReject = async (review: AvatarReviewQueueEntry) => {
    const reason = window.prompt(`Reject "${review.avatarDefinitionId}"? Enter rejection reason (required):`);
    if (!reason) { alert('Rejection reason is required'); return; }
    const notes = window.prompt('Add additional review notes (optional):');
    if (notes === null) return;
    try {
      await rejectReview(review.avatarDefinitionId, review.reviewId, user?.userId || '', reason, notes || undefined);
      alert('Avatar submission rejected');
      fetchReviews();
      fetchStats();
      setShowDetailModal(false);
    } catch (err: any) {
      alert(`Failed to reject: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-background-light">
      <div className="w-full max-w-[1400px] mx-auto px-6 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-text-sub mb-6">
          <button onClick={() => window.history.back()} className="hover:text-primary transition-colors">Admin</button>
          <ChevronRight className="w-4 h-4" />
          <span className="text-text-main font-medium">Avatar Review Queue</span>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text-main mb-2">Avatar Review Queue</h1>
          <p className="text-text-sub">Review and approve avatar submissions for your organization catalog</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-amber-50 rounded-lg">
                  <Clock className="w-5 h-5 text-amber-500" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Pending</span>
              </div>
              <p className="text-3xl font-bold text-text-main">{stats.pending}</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-emerald-50 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Approved</span>
              </div>
              <p className="text-3xl font-bold text-text-main">{stats.approved}</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-500" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Rejected</span>
              </div>
              <p className="text-3xl font-bold text-text-main">{stats.rejected}</p>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-blue-50 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-500" />
                </div>
                <span className="text-xs font-bold uppercase tracking-wider text-text-sub">Avg Review</span>
              </div>
              <p className="text-3xl font-bold text-text-main">{stats.averageReviewTime}h</p>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-slate-200 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-text-sub">Loading reviews...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-700">Error loading reviews</p>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Reviews Table */}
        {!loading && !error && reviews.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Avatar</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Submitted By</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Date</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Version</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500">Notes</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {reviews.map((review) => (
                  <tr key={review.reviewId} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <Users className="w-4 h-4 text-primary" />
                        </div>
                        <p className="text-sm font-semibold text-text-main truncate max-w-[200px]">
                          {review.avatarDefinitionId}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-text-sub">{review.submittedBy}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-text-sub">{new Date(review.submittedAt).toLocaleDateString()}</span>
                    </td>
                    <td className="px-6 py-5">
                      <span className="text-sm text-text-sub">v{review.versionNumber}</span>
                    </td>
                    <td className="px-6 py-5">
                      {review.submissionNotes ? (
                        <p className="text-sm text-text-sub italic truncate max-w-[200px]">"{review.submissionNotes}"</p>
                      ) : (
                        <span className="text-sm text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-6 py-5 text-right">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => handleViewDetails(review)}
                          className="px-3 py-2 rounded-lg bg-navy text-white text-xs font-medium hover:bg-navy-dark transition-colors flex items-center gap-1.5"
                        >
                          <Eye className="w-3.5 h-3.5" /> Review
                        </button>
                        <button
                          onClick={() => handleApprove(review)}
                          className="px-3 py-2 rounded-lg bg-emerald-500 text-white text-xs font-medium hover:bg-emerald-600 transition-colors flex items-center gap-1.5"
                        >
                          <CheckCircle className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button
                          onClick={() => handleReject(review)}
                          className="px-3 py-2 rounded-lg bg-red-500 text-white text-xs font-medium hover:bg-red-600 transition-colors flex items-center gap-1.5"
                        >
                          <XCircle className="w-3.5 h-3.5" /> Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && reviews.length === 0 && (
          <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-16 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-7 h-7 text-emerald-400" />
            </div>
            <h3 className="text-lg font-semibold text-text-main mb-2">No pending reviews</h3>
            <p className="text-sm text-text-sub max-w-sm mx-auto">
              All avatar submissions have been reviewed. Great work!
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedReview && selectedAvatar && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6"
          onClick={() => setShowDetailModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl max-w-[800px] w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-text-main">Review Avatar</h2>
                  <p className="text-sm text-text-sub">{selectedAvatar.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Description */}
              <div>
                <h3 className="text-sm font-semibold text-text-main mb-2">Description</h3>
                <p className="text-sm text-text-sub leading-relaxed">{selectedAvatar.description}</p>
              </div>

              {/* System Prompt */}
              <div>
                <h3 className="text-sm font-semibold text-text-main mb-2">System Prompt</h3>
                <pre className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-text-sub whitespace-pre-wrap break-words max-h-[200px] overflow-y-auto">
                  {selectedAvatar.systemPrompt}
                </pre>
              </div>

              {/* Metadata Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Category</p>
                  <p className="text-sm font-medium text-text-main">{selectedAvatar.category}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Backend Type</p>
                  <p className="text-sm font-medium text-text-main">{selectedAvatar.backendType}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">TTS Provider</p>
                  <p className="text-sm font-medium text-text-main">{selectedAvatar.ttsProvider}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-xs font-medium text-text-sub uppercase tracking-wider mb-1">Max Response</p>
                  <p className="text-sm font-medium text-text-main">{selectedAvatar.maxResponseLength} words</p>
                </div>
              </div>

              {/* Keywords */}
              <div>
                <h3 className="text-sm font-semibold text-text-main mb-2">Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedAvatar.keywords.map((keyword) => (
                    <span key={keyword} className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>

              {/* Feature Badges */}
              <div className="flex flex-wrap gap-2">
                {selectedAvatar.supportsPhysicalDevice && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700">
                    Physical Device Support
                  </span>
                )}
                {selectedAvatar.isPublic && (
                  <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                    Public
                  </span>
                )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-slate-200">
              <button
                onClick={() => setShowDetailModal(false)}
                className="flex-1 px-4 py-2.5 rounded-lg border border-slate-300 text-text-main hover:bg-slate-50 font-medium transition-colors text-sm"
              >
                Close
              </button>
              <button
                onClick={() => handleApprove(selectedReview)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-medium transition-colors text-sm flex items-center justify-center gap-2"
              >
                <CheckCircle className="w-4 h-4" /> Approve
              </button>
              <button
                onClick={() => handleReject(selectedReview)}
                className="flex-1 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 text-white font-medium transition-colors text-sm flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" /> Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
